import clsx from 'clsx';
import { useField } from 'formik';
import { CognitiveMode } from 'generated/graphql';
import {
  isV1CognitiveSkillsVersion,
  TCognitiveSkillsVersion,
} from 'helpers/cognitiveSkills';
import {
  FormikRadioButtonGroup,
  InlineNotification,
  NotificationType,
} from '@spotted-zebra-uk/ui-components';
import styles from './StageForm.module.scss';

const COGNITIVE_MODE_OPTIONS = [
  {
    id: 'cognitive-mode-in-depth',
    value: CognitiveMode.InDepth,
    label: 'In-depth',
    title: 'Outputs a summary and individual cognitive scores.',
    description: 'Best for higher score accuracy with a longer assessment.',
  },
  {
    id: 'cognitive-mode-streamlined',
    value: CognitiveMode.Streamlined,
    label: 'Streamlined',
    title: 'Outputs a summary score only.',
    description:
      'Best for a faster assessment experience with slightly lower score accuracy.',
  },
];

const CognitiveModeField = ({
  hasRoleOnlyOneCognitiveSkill,
  cognitiveSkillsVersion,
}: {
  hasRoleOnlyOneCognitiveSkill: boolean;
  cognitiveSkillsVersion: TCognitiveSkillsVersion;
}) => {
  const isDisabled =
    hasRoleOnlyOneCognitiveSkill ||
    isV1CognitiveSkillsVersion(cognitiveSkillsVersion);

  return (
    <section
      className={clsx(
        styles.cognitiveModeFieldSection,
        isDisabled && styles.disabledCognitiveModeFieldSection,
      )}
    >
      <FormikRadioButtonGroup
        id="cognitiveMode"
        name="cognitiveMode"
        options={COGNITIVE_MODE_OPTIONS}
        useFormikField={useField}
        className={styles.cognitiveModeRadioGroup}
        disabled={isDisabled}
      />
      <div className={styles.cognitiveModeGuidanceContainer}>
        {COGNITIVE_MODE_OPTIONS.map((option) => (
          <div key={option.value}>
            <div className={styles.cognitiveModeOptionTitle}>
              {option.title}
            </div>
            <div className={styles.cognitiveModeOptionDescription}>
              {option.description}
            </div>
          </div>
        ))}
      </div>
      {isDisabled && (
        <InlineNotification
          notificationType={NotificationType.NEUTRAL}
          className={styles.cognitiveModeNotification}
        >
          {isV1CognitiveSkillsVersion(cognitiveSkillsVersion) ? (
            <p>
              This role uses V1 cognitive abilities, which are only compatible
              with the in-depth assessment mode. Streamlined mode isn't
              supported for V1 abilities.
            </p>
          ) : (
            <p>
              One or more of the Role Skills Profiles you've added include only
              a single cognitive assessment. To ensure score validity, the
              In-depth package is required.
            </p>
          )}
        </InlineNotification>
      )}
    </section>
  );
};

export default CognitiveModeField;
